import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Row, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';

import { CollectionChannelConfig } from 'constants/constants';
import {
  debtCollectionApi,
  DebtCollectionChannelConfigDto,
  CollectionChannel,
} from '../debtCollectionApi';
import ChannelConfigModal from './ChannelConfigModal';

const ALL_CHANNELS = [
  CollectionChannel.Email,
  CollectionChannel.Sms,
  CollectionChannel.WhatsApp,
  CollectionChannel.Telegram,
  CollectionChannel.Push,
  CollectionChannel.InternalNotification,
  CollectionChannel.Webhook,
];

const ChannelsTab: React.FC = () => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState<DebtCollectionChannelConfigDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState<{
    visible: boolean;
    channel: CollectionChannel;
    config: DebtCollectionChannelConfigDto | null;
  }>({ visible: false, channel: CollectionChannel.Email, config: null });

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await debtCollectionApi.getChannels();
      setChannels(data);
    } catch (error) {
      console.error('Error fetching channels:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  const getChannelConfig = (ch: CollectionChannel) =>
    channels.find((c) => c.channel === ch) ?? null;

  const handleConfigure = (ch: CollectionChannel) => {
    setModalState({ visible: true, channel: ch, config: getChannelConfig(ch) });
  };

  const handleModalSuccess = () => {
    setModalState((prev) => ({ ...prev, visible: false }));
    fetchChannels();
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        {ALL_CHANNELS.map((ch) => {
          const cfg = CollectionChannelConfig[ch];
          const channelData = getChannelConfig(ch);
          const isEnabled = channelData?.isEnabled ?? false;

          return (
            <Col xs={24} sm={12} md={8} lg={6} key={ch}>
              <Card
                hoverable
                style={{ textAlign: 'center' }}
                onClick={() => handleConfigure(ch)}
              >
                <div style={{ marginBottom: 12 }}>
                  <FeatherIcon icon={cfg.icon} size={32} />
                </div>
                <h4 style={{ marginBottom: 8 }}>{cfg.title}</h4>
                <Badge
                  status={isEnabled ? 'success' : 'default'}
                  text={isEnabled
                    ? t('global:enabled', 'Enabled')
                    : t('global:disabled', 'Disabled')}
                />
                <div style={{ marginTop: 12 }}>
                  <Button size="small" type="link">
                    {t('debt-collection:channels.configure', 'Configure')}
                  </Button>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      <ChannelConfigModal
        visible={modalState.visible}
        channel={modalState.channel}
        config={modalState.config}
        onCancel={() => setModalState((prev) => ({ ...prev, visible: false }))}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default ChannelsTab;
